import { useState } from 'react'

import { Card } from '@/shared/components/ui/card'

import { confirmLeadFollowUp } from '../../api/sales.api'
import { useSalesDashboardStore } from '../../stores/dashboard.store'

function LeadNotFollowedItem({ lead }) {
  const sourceLabel = lead.source || '—'

  const isOffline =
    lead.online === false ||
    String(lead.sourceKey || lead.source || '').toLowerCase() === 'offline'

  const [isConfirming, setIsConfirming] = useState(false)

  const removeLeadFromFollowedUp = useSalesDashboardStore(
    (state) => state.removeLeadFromFollowedUp,
  )

  const handleConfirmFollowUp = async () => {
    if (isConfirming) return

    try {
      setIsConfirming(true)

      await confirmLeadFollowUp(lead._id)

      removeLeadFromFollowedUp(lead._id)
    } catch (error) {
      console.error('Failed to confirm follow-up:', error)
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <div className="flex min-h-[76px] items-center justify-between gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
      {/* Lead information */}
      <div className="flex min-w-0 items-center gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${
            lead.avatarColor || 'from-orange-400 to-orange-500'
          } text-[13px] font-bold text-white`}
        >
          {lead.initials || '—'}
        </div>

        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-slate-900">
            {lead.name}
          </p>

          <p className="truncate text-[11px] text-slate-400">
            {lead.company}
            {lead.pending ? ` · pending ${lead.pending}` : ''}
          </p>
        </div>
      </div>

      {/* Source + confirmation */}
      <div className="flex shrink-0 flex-col items-end gap-2">
        {isOffline ? (
          <span className="rounded-full bg-slate-900 px-2.5 py-1 text-[11px] font-medium text-white">
            Offline
          </span>
        ) : (
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
            {sourceLabel}
          </span>
        )}

        <button
          type="button"
          onClick={handleConfirmFollowUp}
          disabled={isConfirming}
          className="rounded-full bg-emerald-500 px-3 py-1.5 text-[11px] font-medium text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isConfirming ? 'Confirming...' : 'Followed Up'}
        </button>
      </div>
    </div>
  )
}

export default function LeadsNotFollowedUpCard({ data = [] }) {
  const list = Array.isArray(data) ? data : []

  return (
    <Card className="border border-amber-200 bg-amber-50/40 p-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />

          <h3 className="text-sm font-semibold text-slate-900">
            Leads Followed Up
          </h3>

          <span className="rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-semibold text-white">
            {list.length}
          </span>
        </div>

        <p className="mt-1 text-[11px] text-slate-400">
          Admin followed up — reach out before the lead goes cold
        </p>
      </div>

      {/* Leads */}
      <div className="mt-4 max-h-[420px] flex-1 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:max-h-none">
        {list.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-slate-400">
            No followed-up leads
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {list.map((lead, index) => (
              <LeadNotFollowedItem
                key={lead._id ?? lead.name ?? index}
                lead={lead}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}
